import { Button, Dialog } from "@mui/material";
import CsvAiPromptBox from "controls/CsvAiPromptBox";
import CsvPreviewTable, { missingCsvHeaders, parseCsv } from "controls/CsvPreviewTable";
import LinkButton from "controls/LinkButton";
import Message from "controls/Message";
import UploaderInput from "inputs/UploaderInput";
import React, { useState } from "react";

export default function CsvImportDialog({
  title,
  infoMessage,
  columnValidators = {},
  columnTypes = {},
  requiredHeaders = [],
  previewMaxRows = 500,
  onDownloadTemplate,
  aiPrompt,
  onSubmit,
  renderSuccess,
  onClose,
  children,
}) {
  const [phase, setPhase] = useState("upload");
  const [uploaderValue, setUploaderValue] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState(null);
  const [csvRows, setCsvRows] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const handleUploaderChange = async (value) => {
    setUploaderValue(value);
    setCsvHeaders(null);
    setCsvRows(null);
    setErrorMessage(null);
    if (!value?.blobUrl) return;
    try {
      const text = await fetch(value.blobUrl).then((r) => r.text());
      const { headers, rows } = parseCsv(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
    } catch (e) {
      setErrorMessage(`Could not read CSV file: ${e.message}`);
    }
  };

  const previewHidden = csvRows && csvRows.length > previewMaxRows;

  const missingHeaders = missingCsvHeaders(requiredHeaders, csvHeaders);
  const headerError = missingHeaders.length
    ? `This file is missing required column(s): ${missingHeaders.join(", ")}. Please upload a CSV that matches the template.`
    : null;

  // For large files we skip the preview and client-side validation entirely; the
  // backend validates every row and emails the uploader about any that fail.
  const hasValidationErrors =
    !previewHidden &&
    (csvRows?.some((row) => csvHeaders?.some((h, i) => columnValidators[h]?.(row[i] ?? "") != null)) ?? false);

  const canSubmit = csvHeaders && csvRows && csvRows.length > 0 && !headerError && !hasValidationErrors && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const result = await onSubmit(csvHeaders, csvRows, uploaderValue);
      setSubmitResult(result);
      setPhase("done");
    } catch (error) {
      setErrorMessage(error.message.replace("GraphQL error: ", ""));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      maxWidth="lg"
      fullWidth
      scroll="body"
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose?.();
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>
        <div style={{ fontSize: "1.2em", fontWeight: "bold" }}>{title}</div>

        {phase === "upload" && (
          <>
            <Message
              type="info"
              content={
                <>
                  {infoMessage} <br />
                  {onDownloadTemplate && (
                    <>
                      You can <LinkButton onClick={onDownloadTemplate}>download</LinkButton> the template CSV.
                    </>
                  )}
                </>
              }
            />

            {aiPrompt && <CsvAiPromptBox {...aiPrompt} />}

            {errorMessage && <Message type="error" content={errorMessage} />}

            <UploaderInput
              name="assetUploader"
              label="CSV Upload"
              value={uploaderValue}
              onChange={handleUploaderChange}
              accept=".csv"
            />

            {headerError && <Message type="error" content={headerError} />}

            {csvHeaders &&
              csvRows &&
              (previewHidden ? (
                <Message
                  type="info"
                  content={`Preview unavailable — file has ${csvRows.length.toLocaleString()} rows.`}
                />
              ) : (
                <CsvPreviewTable
                  headers={csvHeaders}
                  rows={csvRows}
                  onChange={setCsvRows}
                  columnValidators={columnValidators}
                  columnTypes={columnTypes}
                />
              ))}

            {children}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? "Importing…" : "Confirm & Import"}
              </Button>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
            </div>
          </>
        )}

        {phase === "done" && (
          <>
            {typeof renderSuccess === "function" ? renderSuccess(submitResult) : renderSuccess}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
